import React, { useState, useEffect, useRef } from "react";
import {
  assignedImageIdSet,
  createEmptyShot,
  ShotArrange,
  shotImageIds,
} from "@beleef/editor-shot";
import axios from "utils/axios";
import { FaFileUpload, FaArrowLeft } from "react-icons/fa";
import { FiFolderPlus } from "react-icons/fi";
import { HiChevronRight } from "react-icons/hi2";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import { useRawImageConversion } from "apis/public";
import { useDownloadManager } from "hooks/useDownloadManager";
import DownloadProgressToast from "components/ui/DownloadProgressToast";
import ImageContainer from "components/upload/ImageContainer";
import Lightbox from "components/ui/Lightbox";
import HelpGuide from "components/ui/HelpGuide";

const rawFormats = [
  ".cr3",
  ".cr2",
  ".dng",
  ".nef",
  ".arw",
  ".raf",
  ".rw2",
  ".orf",
  ".pef",
];

const toLocalImageId = (serverId) => `raw-${serverId}`;

const ImageUpload = ({ eventProp }) => {
  let editCount = 0;
  for (const svc of eventProp.services || []) {
    editCount += svc.quantity || 0;
  }

  const [images, setImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [uploadProgress, setUploadProgress] = useState({});
  const [activeFolder, setActiveFolder] = useState(null);
  const [allImagesUploaded, setAllImagesUploaded] = useState(false);
  const [editConfirm, setEditConfirm] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const fileInputId = `file-upload-${eventProp._id}`;
  const [notes, setNotes] = useState("");
  // Shot arrangement (local image ids) — mark M/E then + Shot.
  const [shots, setShots] = useState([]);
  const shotsLoadedRef = useRef(false);

  const isCompleted = [
    "completed",
    "revisionSent",
    "revisionInProgress",
    "revisionCompleted",
  ].includes(eventProp?.status);

  const { downloads, isDownloading, queueDownload, removeDownload } =
    useDownloadManager();
  const [processedImages, setProcessedImages] = useState(new Set());
  const [processingImages, setProcessingImages] = useState(new Set());

  const rawImageMutation = useRawImageConversion({
    onSuccess: (data) => {
      setImages((prev) =>
        prev.map((img) =>
          img.id === data.imageId
            ? { ...img, originalUrl: data.originalUrl, url: data.convertedUrl }
            : img
        )
      );
      setProcessedImages((prev) => new Set([...prev, data.imageId]));
    },
    onError: (error) => console.error("Error processing image:", error),
    onSettled: (data, error, variables) => {
      setProcessingImages((prev) => {
        const s = new Set(prev);
        s.delete(variables.imageId);
        return s;
      });
    },
  });

  useEffect(() => {
    setAllImagesUploaded(
      images.length > 0 && images.every((img) => img.isUploaded)
    );
  }, [images]);

  useEffect(() => {
    if (eventProp.rawPhotos || eventProp.extraPhotos) {
      const allImages = [
        ...(eventProp.rawPhotos?.map((img, i) => {
          let imageUrl = img.url;
          let originalUrl = img.url;
          const isRaw = rawFormats.some((ext) =>
            img.name?.toLowerCase().endsWith(ext)
          );
          if (isRaw && img.revisions?.length > 0) {
            const origRev = img.revisions.find((r) => r.isOriginal);
            if (origRev) originalUrl = origRev.url;
            if (img.activeRevision) {
              const active = img.revisions.find(
                (r) => r._id === img.activeRevision
              );
              if (active) imageUrl = active.url;
            } else {
              imageUrl = img.revisions[0].url;
            }
          }
          let s3Key = img.s3Key || null;
          if (!s3Key && img.url && img.url.includes(".amazonaws.com/")) {
            s3Key = img.url.split(".amazonaws.com/")[1];
          }
          return {
            id: toLocalImageId(img.id),
            serverImageId: img.id,
            url: imageUrl,
            originalUrl,
            name: img.name || `Image ${i + 1}`,
            folder: "raw",
            isUploaded: true,
            size: img.size,
            s3Key,
          };
        }) || []),
        ...(eventProp.extraPhotos?.map((img, i) => ({
          id: `extra-${i + 1}`,
          serverImageId: img.id,
          url: img.url,
          name: img.name || `Image ${i + 1}`,
          folder: "extras",
          isUploaded: true,
          size: img.size,
        })) || []),
      ];

      setImages(allImages);

      if (!shotsLoadedRef.current && eventProp.editorShots?.length) {
        shotsLoadedRef.current = true;
        const mapped = eventProp.editorShots.map((shot, index) => ({
          ...createEmptyShot(index),
          ...shot,
          mainImageId:
            shot.mainImageId != null ? toLocalImageId(shot.mainImageId) : null,
          exposureImageIds: (shot.exposureImageIds || []).map(toLocalImageId),
        }));
        if (mapped.length) {
          setShots(mapped);
        }
      }
    }
  }, [eventProp]);

  // CR3 rendering
  useEffect(() => {
    if (!activeFolder) return;
    const render = async () => {
      const unprocessed = images.filter(
        (img) =>
          img.url &&
          !img.url.includes("blob") &&
          img.url.includes("https") &&
          img.isUploaded &&
          rawFormats.some((ext) => img.name?.toLowerCase().endsWith(ext)) &&
          rawFormats.some((ext) => img.url?.toLowerCase().endsWith(ext)) &&
          !processedImages.has(img.id) &&
          !processingImages.has(img.id)
      );
      if (unprocessed.length === 0) return;
      setProcessingImages(
        (prev) => new Set([...prev, ...unprocessed.map((i) => i.id)])
      );
      for (let i = 0; i < unprocessed.length; i += 3) {
        await Promise.all(
          unprocessed.slice(i, i + 3).map((img) =>
            rawImageMutation
              .mutateAsync({
                imageUrl: img.url,
                imageId: img.id,
                revisionId: null,
              })
              .catch(() => {})
          )
        );
        if (i + 3 < unprocessed.length)
          await new Promise((r) => setTimeout(r, 1000));
      }
    };
    render();
  }, [activeFolder, images, processedImages, processingImages]);

  const handleFileUpload = async (event) => {
    const newFiles = Array.from(event.target.files).map((file, i) => ({
      id: Date.now() + i,
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      folder: activeFolder,
      isUploaded: false,
      s3Key: null,
      eventId: eventProp._id,
    }));
    setImages((prev) => [...prev, ...newFiles]);
    event.target.value = "";
    for (const f of newFiles) {
      try {
        const res = await axios.post("/admin/prepareMarketing/fileUpload", {
          files: [{ fileName: f.name, fileType: f.file.type }],
          propertyId: eventProp?.propertyId?._id,
          folderPath: activeFolder === "raw" ? "rawPhotos" : "extraPhotos",
        });
        const presigned = res.data.presignedUrls[0];
        await axios.put(presigned.url, f.file, {
          headers: { "Content-Type": f.file.type },
          onUploadProgress: (e) => {
            setUploadProgress((prev) => ({
              ...prev,
              [f.id]: Math.round((e.loaded * 100) / e.total),
            }));
          },
        });
        const s3Url = presigned.url.split("?")[0];
        setImages((prev) =>
          prev.map((img) =>
            img.id === f.id
              ? { ...img, isUploaded: true, s3Key: presigned.key, s3Url }
              : img
          )
        );
        if (rawFormats.some((ext) => f.name.toLowerCase().endsWith(ext)))
          rawImageMutation.mutate({
            imageUrl: s3Url,
            imageId: f.id,
            revisionId: null,
          });
      } catch (error) {
        console.error(`Failed to upload ${f.name}:`, error);
      }
    }
  };

  const removeImageFromShots = (imageId) => {
    setShots((prev) =>
      prev.map((shot) => ({
        ...shot,
        mainImageId:
          String(shot.mainImageId) === String(imageId)
            ? null
            : shot.mainImageId,
        exposureImageIds: (shot.exposureImageIds || []).filter(
          (id) => String(id) !== String(imageId)
        ),
      }))
    );
  };

  const deleteImage = async (imageId) => {
    const image = images.find((img) => img.id === imageId);
    if (!image) return;
    try {
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      removeImageFromShots(imageId);
      await axios.post("/admin/prepareMarketing/deleteS3File", {
        key: image?.s3Key,
        name: image.name,
        eventId: eventProp?._id,
        propertyId: eventProp?.propertyId?._id,
        field: activeFolder === "raw" ? "rawPhotos" : "extraPhotos",
      });
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  const deleteAllImages = async () => {
    const uploaded = images.filter(
      (img) =>
        (img.isUploaded && img.s3Key) ||
        (img.url && img.folder === activeFolder)
    );
    try {
      setImages([]);
      setShots([]);
      await Promise.all(
        uploaded.map((img) =>
          axios.post("/admin/prepareMarketing/deleteS3File", {
            key: img.s3Key,
            name: img.name,
            eventId: eventProp?._id,
            propertyId: eventProp?.propertyId?._id,
            field: activeFolder === "raw" ? "rawPhotos" : "extraPhotos",
          })
        )
      );
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  const getCurrentFolderImages = () =>
    images.filter((img) => img.folder === activeFolder);

  const getAssignedImageIds = () => assignedImageIdSet(shots);

  const notifyEditor = async () => {
    try {
      setNotifyLoading(true);
      const assignedIds = getAssignedImageIds();

      const allShots = shots
        .filter((s) => s.mainImageId != null)
        .map((shot, index) => {
          const keys = shotImageIds(shot)
            .map((localId) => {
              const img = images.find((i) => i.id === localId);
              return img?.s3Key || null;
            })
            .filter(Boolean);
          return {
            shotId: shot.shotId,
            label: shot.label || `Shot ${index + 1}`,
            s3Keys: keys,
          };
        })
        .filter((shot) => shot.s3Keys.length > 0);

      const rawImgs = images
        .filter(
          (img) =>
            img.isUploaded &&
            img.folder === activeFolder &&
            img.s3Key &&
            assignedIds.has(String(img.id))
        )
        .map((img) => ({ s3Key: img.s3Key, name: img.name, size: img.size }));
      const extraImgs = images
        .filter(
          (img) =>
            img.isUploaded &&
            img.folder === activeFolder &&
            img.s3Key &&
            !assignedIds.has(String(img.id))
        )
        .map((img) => ({ s3Key: img.s3Key, name: img.name, size: img.size }));
      const res = await axios.post("/admin/prepareMarketing/notifyEditor", {
        event: eventProp,
        images: rawImgs,
        folderPath: "rawPhotos",
        notes,
        isExtras: extraImgs.length > 0,
        shots: allShots,
      });
      if (res.data.success) {
        if (extraImgs.length > 0)
          await axios.put(
            `/admin/prepareMarketing/${eventProp?.propertyId?._id}`,
            { event: eventProp, images: extraImgs, folderPath: "extraPhotos" }
          );
        showToast("success", "Send to editor successfully");
        setEditConfirm(false);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setNotifyLoading(false);
    }
  };

  const [extrasLoading, setExtrasLoading] = useState(false);

  const handleExtras = async () => {
    try {
      setExtrasLoading(true);
      const finalImages = images
        .filter(
          (img) => img.isUploaded && img.folder === activeFolder && img.s3Key
        )
        .map((img) => ({ s3Key: img.s3Key, name: img.name, size: img.size }));
      if (finalImages.length > 0) {
        const res = await axios.put(
          `/admin/prepareMarketing/${eventProp?.propertyId?._id}`,
          {
            event: eventProp,
            images: finalImages,
            folderPath: activeFolder === "raw" ? "rawPhotos" : "extraPhotos",
          }
        );
        if (res.data.success) showToast("success", "Save successfully");
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setExtrasLoading(false);
    }
  };

  const currentFolderImages = getCurrentFolderImages();

  return (
    <>
      {!activeFolder ? (
        <div className="flex gap-3">
          <div
            className="cursor-pointer flex flex-col items-center gap-1.5 bg-lightergray py-3 px-6 rounded-lg hover:bg-lightgray transition-colors border border-mediumgray"
            onClick={() => setActiveFolder("raw")}
          >
            <FiFolderPlus className="w-8 h-8 text-darkergray" />
            <p className="text-xs font-semibold text-black">Raw</p>
          </div>
          {(eventProp?.extraPhotos?.length > 0 ||
            (eventProp?.status && eventProp?.status !== "scheduled")) && (
            <div
              className="cursor-pointer flex flex-col items-center gap-1.5 bg-lightergray py-3 px-6 rounded-lg hover:bg-lightgray transition-colors border border-mediumgray"
              onClick={() => setActiveFolder("extras")}
            >
              <FiFolderPlus className="w-8 h-8 text-darkergray" />
              <p className="text-xs font-semibold text-black">Extras</p>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setActiveFolder(null)}
                className="gray-button flex items-center"
              >
                <FaArrowLeft className="mr-1.5" /> Back
              </Button>
              <span className="text-xs text-darkergray font-medium flex items-center">
                <HiChevronRight className="mx-1 w-3 h-3" /> {activeFolder}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {activeFolder === "raw" && (
                <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  className="text-xs font-semibold text-darkergray hover:text-black border-0 bg-transparent cursor-pointer px-2 py-1"
                >
                  How it works
                </button>
              )}
              {(eventProp?.status === "scheduled" ||
                activeFolder !== "raw") && (
                <>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id={fileInputId}
                  />
                  <label
                    htmlFor={fileInputId}
                    className="flex items-center black-button cursor-pointer"
                  >
                    <FaFileUpload className="mr-1.5" /> Upload
                  </label>
                </>
              )}
              {currentFolderImages.length > 0 &&
                (eventProp?.status === "scheduled" ||
                  activeFolder !== "raw") && (
                  <Button className="outline-button" onClick={deleteAllImages}>
                    Delete All
                  </Button>
                )}
            </div>
          </div>

          {activeFolder === "raw" && (
            <div className="text-xs text-darkergray mb-3">
              Arrange {editCount} shot{editCount === 1 ? "" : "s"}
            </div>
          )}

          {isCompleted && currentFolderImages.length > 0 && (
            <div className="flex items-center justify-end mb-3">
              <Button
                onClick={() =>
                  queueDownload(
                    currentFolderImages.map((img) => ({
                      ...img,
                      url: img.originalUrl || img.url,
                    })),
                    `${activeFolder}-${eventProp?.propertyId?.address}-${eventProp?.summary}`
                  )
                }
                className="black-button flex items-center"
                disabled={isDownloading}
              >
                {isDownloading ? "Downloading..." : "Download All"}
              </Button>
            </div>
          )}

          {activeFolder === "raw" && currentFolderImages.length > 0 && (
            <ShotArrange
              images={currentFolderImages.filter((img) => img.isUploaded)}
              maxShots={editCount}
              shots={shots}
              readOnly={eventProp?.status !== "scheduled"}
              allowExposure
              onDelete={
                eventProp?.status === "scheduled" ? deleteImage : undefined
              }
              onShotsChange={setShots}
              onViewLarge={(image) => {
                const idx = currentFolderImages.findIndex(
                  (img) => String(img.id) === String(image.id),
                );
                if (idx >= 0) setLightboxIndex(idx);
              }}
            />
          )}

          <div className="mb-3">
            <textarea
              className="form-textarea border border-mediumgray"
              rows={2}
              placeholder="Notes for editor..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {activeFolder !== "raw" && (
            <div className="grid gap-3 mb-4 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {currentFolderImages.map((image, index) => (
                <ImageContainer
                  key={image.id}
                  image={image}
                  onDelete={(id) => deleteImage(id)}
                  onPreview={() => setLightboxIndex(index)}
                  viewMode="grid"
                  progress={uploadProgress[image.id] || 0}
                  showDelete
                  showDownload={isCompleted}
                  showTypeSelect={false}
                  isSelected={getAssignedImageIds().has(String(image.id))}
                  eventId={eventProp?._id}
                  propertyId={eventProp?.propertyId?._id}
                />
              ))}
            </div>
          )}

          {activeFolder === "raw" &&
            currentFolderImages.some((img) => !img.isUploaded) && (
              <div className="grid gap-3 mb-4 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {currentFolderImages
                  .filter((img) => !img.isUploaded)
                  .map((image) => {
                    const index = currentFolderImages.findIndex(
                      (img) => img.id === image.id,
                    );
                    return (
                      <ImageContainer
                        key={image.id}
                        image={image}
                        onDelete={(id) => deleteImage(id)}
                        onPreview={() => setLightboxIndex(index)}
                        viewMode="grid"
                        progress={uploadProgress[image.id] || 0}
                        showDelete
                        showDownload={false}
                        showTypeSelect={false}
                        eventId={eventProp?._id}
                        propertyId={eventProp?.propertyId?._id}
                      />
                    );
                  })}
              </div>
            )}

          {(eventProp?.status === "scheduled" || activeFolder !== "raw") &&
            currentFolderImages.length > 0 && (
              <div className="flex justify-end">
                {activeFolder === "raw" ? (
                  <Button
                    onClick={() => {
                      const assignedIds = getAssignedImageIds();
                      const withMain = shots.filter(
                        (s) => s.mainImageId != null
                      );
                      if (assignedIds.size === 0) {
                        showToast("error", "Please arrange images into shots");
                        return;
                      }
                      if (withMain.length < editCount) {
                        showToast(
                          "error",
                          `Arrange at least ${editCount} shot(s) with a main`
                        );
                        return;
                      }
                      if (withMain.length > editCount) {
                        showToast(
                          "error",
                          `Too many shots (${withMain.length}/${editCount})`
                        );
                        return;
                      }
                      setEditConfirm(true);
                    }}
                    className="black-button flex"
                    disabled={!allImagesUploaded}
                  >
                    {!allImagesUploaded ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin mr-2" />{" "}
                        Uploading
                      </>
                    ) : (
                      "Send to Editor"
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleExtras}
                    className="black-button flex"
                    disabled={!allImagesUploaded}
                    loading={extrasLoading}
                  >
                    {!allImagesUploaded ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin mr-2" />{" "}
                        Uploading
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                )}
              </div>
            )}
        </div>
      )}

      <DownloadProgressToast downloads={downloads} onRemove={removeDownload} />
      <Lightbox
        images={currentFolderImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxIndex >= 0}
        onClose={() => setLightboxIndex(-1)}
      />

      {helpOpen && (
        <HelpGuide mode="contractor" onClose={() => setHelpOpen(false)} />
      )}

      <Modal
        isOpen={editConfirm}
        onClose={() => setEditConfirm(false)}
        title="Send to Editor"
      >
        <div className="flex flex-col space-y-4">
          <p className="text-sm text-darkergray">
            Once sent this cannot be undone. HDR merge will run automatically in
            the background for bracketed shots.
          </p>
          <div className="border border-mediumgray rounded-xl p-3 text-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">Shots</span>
              <span className="text-darkergray">
                {shots.filter((s) => s.mainImageId != null).length}/{editCount}
              </span>
            </div>
            <div className="border-t border-mediumgray pt-2 flex items-center justify-between text-darkergray">
              <span>Extras (unassigned)</span>
              <span>
                {
                  currentFolderImages.filter(
                    (img) =>
                      img.folder === "raw" &&
                      img.isUploaded &&
                      !getAssignedImageIds().has(String(img.id)),
                  ).length
                }{" "}
                images
              </span>
            </div>
          </div>
          <div className="w-full flex gap-2 justify-end">
            <Button
              onClick={() => setEditConfirm(false)}
              className="gray-button"
            >
              Cancel
            </Button>
            <Button
              onClick={notifyEditor}
              className="black-button"
              loading={notifyLoading}
            >
              Send to Editor
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ImageUpload;
